// Modal dla globalnego narzutu/rabatu na noclegi

import React, { useState, useEffect } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { MarkupSettings } from "../../types/pricing.types";
import { toFiniteNumber } from "../../../../utils/markupCalculations";

const SAVE_ERROR_MESSAGE = "Nie udało się zapisać narzutu. Spróbuj ponownie.";

interface GlobalMarkupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalMarkupModal: React.FC<GlobalMarkupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { markup, applyMarkup, assignedRooms } = useAccommodation();

  // Wartości zapisane w ofercie (API zwraca kolumny decimal jako tekst, np. "5.00")
  const savedType = markup?.accommodationMarkupType || "percentage";
  const savedValue = toFiniteNumber(markup?.accommodationMarkupValue);
  const savedSurcharge = toFiniteNumber(markup?.weekendSurchargePercent);

  // Stan formularza (pola jako tekst, puste pole = 0)
  const [markupType, setMarkupType] = useState<"percentage" | "amount">(
    savedType
  );
  const [markupText, setMarkupText] = useState<string>(String(savedValue));
  const [surchargeText, setSurchargeText] = useState<string>(
    String(savedSurcharge)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    markup?: string;
    surcharge?: string;
  }>({});

  const markupValue = toFiniteNumber(markupText);
  const surchargeValue = toFiniteNumber(surchargeText);

  // Przy każdym otwarciu pokaż wartości zapisane w ofercie (Anuluj odrzuca zmiany)
  useEffect(() => {
    if (!isOpen) return;
    setMarkupType(savedType);
    setMarkupText(String(savedValue));
    setSurchargeText(String(savedSurcharge));
    setSaveError(null);
    setFieldErrors({});
  }, [isOpen, savedType, savedValue, savedSurcharge]);

  // Oblicz podgląd kosztów
  const costPreview = React.useMemo(() => {
    if (!assignedRooms || assignedRooms.length === 0) {
      return {
        originalCost: 0,
        markupAmount: 0,
        totalCost: 0,
      };
    }

    const originalCost = assignedRooms.reduce(
      (sum, room) => sum + room.quantity * (room.unitPrice || 0),
      0
    );

    let markupAmount = 0;
    if (markupType === "percentage") {
      markupAmount = (originalCost * markupValue) / 100;
    } else {
      markupAmount = markupValue;
    }

    return {
      originalCost,
      markupAmount,
      totalCost: originalCost + markupAmount,
    };
  }, [assignedRooms, markupType, markupValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Zakresy jak w backendzie: narzut -100..999,99, dopłata 0..999,99 (do 2 miejsc)
    const errors: { markup?: string; surcharge?: string } = {};
    if (!(markupValue >= -100 && markupValue <= 999.99)) {
      errors.markup = "Wartość musi mieścić się w zakresie od -100 do 999,99.";
    }
    if (
      !(surchargeValue >= 0 && surchargeValue <= 999.99) ||
      Math.round(surchargeValue * 100) / 100 !== surchargeValue
    ) {
      errors.surcharge =
        "Dopłata musi mieścić się w zakresie od 0 do 999,99 (do 2 miejsc po przecinku).";
    }
    setFieldErrors(errors);
    if (errors.markup || errors.surcharge) return;

    setIsSubmitting(true);
    setSaveError(null);
    try {
      const newMarkup: MarkupSettings = {
        accommodationMarkupType: markupType,
        accommodationMarkupValue: markupValue,
        weekendSurchargePercent: surchargeValue,
      };

      await applyMarkup(newMarkup);
      onClose();
    } catch (error) {
      console.error("Failed to apply markup:", error);
      setSaveError(SAVE_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setMarkupType("percentage");
    setMarkupText("0");
    setSurchargeText("0");
    setFieldErrors({});
    setSaveError(null);
  };

  const handleRemoveMarkup = async () => {
    setIsSubmitting(true);
    setIsRemoving(true);
    setSaveError(null);
    try {
      await applyMarkup(null);
      onClose();
    } catch (error) {
      console.error("Failed to remove markup:", error);
      setSaveError(SAVE_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
      setIsRemoving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={isSubmitting ? undefined : onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              💰 Globalny narzut/rabat
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              ✕
            </button>
          </div>

          {/* Formularz */}
          <form onSubmit={handleSubmit} noValidate className="p-6">
            <div className="space-y-4">
              {/* Hidden radio buttons - global markup can only be percentage type */}
              <input type="hidden" name="markupType" value="percentage" />

              {/* Wartość narzutu */}
              <div>
                <label
                  htmlFor="global-markup-value"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Narzut/rabat globalny
                </label>
                <div className="relative">
                  <input
                    id="global-markup-value"
                    type="number"
                    min="-100"
                    max="999.99"
                    step="any"
                    value={markupText}
                    onChange={(e) => {
                      setMarkupText(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, markup: undefined }));
                    }}
                    aria-invalid={!!fieldErrors.markup}
                    aria-describedby={
                      fieldErrors.markup ? "global-markup-value-error" : undefined
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    placeholder={markupType === "percentage" ? "10" : "100"}
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-500">
                    {markupType === "percentage" ? "%" : "zł"}
                  </span>
                </div>
                {fieldErrors.markup && (
                  <p
                    id="global-markup-value-error"
                    role="alert"
                    className="text-xs text-red-600 mt-1"
                  >
                    {fieldErrors.markup}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {markupType === "percentage"
                    ? "Wartości ujemne = rabat. Np. -10% to rabat 10%"
                    : "Wartości ujemne = rabat. Np. -100 to rabat 100 zł"}
                </p>
              </div>

              {/* Dopłata weekendowa */}
              <div>
                <label
                  htmlFor="global-weekend-surcharge"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Dopłata weekendowa (noce pt i sob)
                </label>
                <div className="relative">
                  <input
                    id="global-weekend-surcharge"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="999.99"
                    step="0.01"
                    value={surchargeText}
                    onChange={(e) => {
                      setSurchargeText(e.target.value);
                      setFieldErrors((prev) => ({
                        ...prev,
                        surcharge: undefined,
                      }));
                    }}
                    aria-invalid={!!fieldErrors.surcharge}
                    aria-describedby={
                      [
                        fieldErrors.surcharge
                          ? "global-weekend-surcharge-error"
                          : "",
                        assignedRooms.length > 0
                          ? "global-weekend-surcharge-note"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    placeholder="10"
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-500">
                    %
                  </span>
                </div>
                {fieldErrors.surcharge && (
                  <p
                    id="global-weekend-surcharge-error"
                    role="alert"
                    className="text-xs text-red-600 mt-1"
                  >
                    {fieldErrors.surcharge}
                  </p>
                )}
                {assignedRooms.length > 0 && (
                  <p
                    id="global-weekend-surcharge-note"
                    className="text-xs text-gray-500 mt-1"
                  >
                    Dotyczy pokoi dodawanych od teraz. Ceny już dodanych pokoi
                    się nie zmienią.
                  </p>
                )}
              </div>

              {/* Podgląd kosztów */}
              {assignedRooms.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    📊 Podgląd kosztów
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Koszt bazowy:</span>
                      <span>{costPreview.originalCost.toFixed(2)} zł</span>
                    </div>
                    <div className="flex justify-between">
                      <span>
                        {markupValue >= 0 ? "Narzut" : "Rabat"}(
                        {markupType === "percentage"
                          ? `${Math.abs(markupValue)}%`
                          : `${Math.abs(markupValue)} zł`}
                        ):
                      </span>
                      <span
                        className={
                          markupValue >= 0 ? "text-red-600" : "text-green-600"
                        }
                      >
                        {markupValue >= 0 ? "+" : "-"}
                        {Math.abs(costPreview.markupAmount).toFixed(2)} zł
                      </span>
                    </div>
                    <div className="flex justify-between font-medium border-t border-gray-200 pt-2">
                      <span>Koszt końcowy:</span>
                      <span>{costPreview.totalCost.toFixed(2)} zł</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Informacja o braku pokoi */}
              {assignedRooms.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <div className="text-yellow-700">
                    <div className="text-lg mb-1">🏨</div>
                    <div className="text-sm">
                      Brak przypisanych pokoi do obliczenia podglądu
                    </div>
                  </div>
                </div>
              )}
            </div>

            {saveError && (
              <div
                role="alert"
                className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
              >
                {saveError}
              </div>
            )}

            {/* Przyciski */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-6 mt-6 border-t border-gray-200">
              <div className="flex flex-wrap items-center space-x-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  🔄 Resetuj
                </button>
                {(savedValue !== 0 || savedSurcharge !== 0) && (
                  <button
                    type="button"
                    onClick={handleRemoveMarkup}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                  >
                    {isRemoving ? "🗑️ Usuwam..." : "🗑️ Usuń narzut"}
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Zapisuję..." : "Zastosuj"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GlobalMarkupModal;
